import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AttachmentUploadStatus, Conversa, Mensagem } from '../types/messaging.types';

interface MessagingState {
  conversas: Conversa[];
  mensagensPorConversa: Record<string, Mensagem[]>;
  totaisPorConversa: Record<string, number>;
  loadingConversas: boolean;
  loadingMensagens: boolean;
  error: string | null;
  lastEventAt: string | null;
  lastEventId: string | null;
  attachmentUpload: {
    status: AttachmentUploadStatus;
    error: string | null;
  };
}

const initialState: MessagingState = {
  conversas: [],
  mensagensPorConversa: {},
  totaisPorConversa: {},
  loadingConversas: false,
  loadingMensagens: false,
  error: null,
  lastEventAt: null,
  lastEventId: null,
  attachmentUpload: {
    status: 'idle',
    error: null,
  },
};

const messagingSlice = createSlice({
  name: 'messaging',
  initialState,
  reducers: {
    setConversas: (state, action: PayloadAction<Conversa[]>) => {
      state.conversas = action.payload ?? [];
    },
    updateConversa: (state, action: PayloadAction<Conversa>) => {
      const index = state.conversas.findIndex((c) => c.id === action.payload.id);
      if (index !== -1) {
        state.conversas[index] = action.payload;
      } else {
        state.conversas.unshift(action.payload);
      }
      // Reordenar por lastMessageAt
      state.conversas.sort((a, b) => {
        const dateA = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
        const dateB = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
        return dateB - dateA;
      });
    },
    setMensagens: (state, action: PayloadAction<{ conversaId: string; mensagens: Mensagem[] }>) => {
      state.mensagensPorConversa[action.payload.conversaId] = action.payload.mensagens;
    },
    setTotalMensagens: (state, action: PayloadAction<{ conversaId: string; total: number }>) => {
      state.totaisPorConversa[action.payload.conversaId] = action.payload.total;
    },
    addMensagem: (state, action: PayloadAction<Mensagem>) => {
      const { conversaId } = action.payload;
      if (!state.mensagensPorConversa[conversaId]) {
        state.mensagensPorConversa[conversaId] = [];
      }
      const exists = state.mensagensPorConversa[conversaId].some((m) => m.id === action.payload.id);
      if (!exists) {
        state.mensagensPorConversa[conversaId].push(action.payload);
      }
    },
    updateMensagemStatus: (
      state,
      action: PayloadAction<{ id: string; conversaId: string; status: Mensagem['status'] }>,
    ) => {
      const msgs = state.mensagensPorConversa[action.payload.conversaId];
      if (msgs) {
        const msg = msgs.find((m) => m.id === action.payload.id);
        if (msg) {
          msg.status = action.payload.status;
        }
      }
    },
    setLoadingConversas: (state, action: PayloadAction<boolean>) => {
      state.loadingConversas = action.payload;
    },
    setLoadingMensagens: (state, action: PayloadAction<boolean>) => {
      state.loadingMensagens = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setLastEventAt: (state, action: PayloadAction<string>) => {
      state.lastEventAt = action.payload;
    },
    setLastEventId: (state, action: PayloadAction<string>) => {
      state.lastEventId = action.payload;
    },
    setAttachmentUploadStatus: (
      state,
      action: PayloadAction<{ status: AttachmentUploadStatus; error?: string | null }>,
    ) => {
      state.attachmentUpload.status = action.payload.status;
      state.attachmentUpload.error = action.payload.error ?? null;
    },
    resetAttachmentUpload: (state) => {
      state.attachmentUpload.status = 'idle';
      state.attachmentUpload.error = null;
    },
  },
});

export const {
  setConversas,
  updateConversa,
  setMensagens,
  setTotalMensagens,
  addMensagem,
  updateMensagemStatus,
  setLoadingConversas,
  setLoadingMensagens,
  setError,
  setLastEventAt,
  setLastEventId,
  setAttachmentUploadStatus,
  resetAttachmentUpload,
} = messagingSlice.actions;

export default messagingSlice.reducer;
